import React, { useMemo, useState } from "react";
import styled, { keyframes, useTheme } from "styled-components";
import { LanguageNameSpaces } from "@shared/types";
import { BASE_CHART_TIME_RANGES } from "@shared/common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { directionColor, getNewLookTokens } from "common/NewLook/tokens.ts";
import {
  NEW_LOOK_NARROW_PHONE_QUERY,
  NEW_LOOK_PHONE_GUTTER,
} from "common/NewLook/layout.ts";
import NewLookSparkline from "features/portfolio/NewLook/NewLookSparkline.tsx";
import {
  PhoneBrandBarChart,
  PhoneBrandLineChart,
} from "features/dashboard/PhoneDashboard/PhoneBrandCharts.tsx";
import {
  PhoneHeroRanges,
  PhoneHeroToggle,
} from "features/dashboard/PhoneDashboard/PhoneHeroControls.tsx";
import { PHONE_CLOSED_PNL_CHART_ID, PHONE_CLOSED_PNL_TOGGLE_ID } from "testIds";
import type { ClosedPnlGraph } from "features/trades/NewLook/useClosedPnlChartSlots.tsx";

const SPARK_WIDTH = 96;
const NARROW_SPARK_WIDTH = 64;
const SPARK_HEIGHT = 36;
const CHART_VIEWS = ["accumulated", "daily"] as const;
const CHART_VIEW_LABELS = {
  accumulated: "charts.accumulated_profit",
  daily: "charts.profit_by_day",
} as const;

type ChartView = (typeof CHART_VIEWS)[number];

export const ClosedPnlSpark = ({
  graph,
  hidden,
}: {
  graph: ClosedPnlGraph;
  hidden: boolean;
}) => {
  const tokens = getNewLookTokens(useTheme());
  const points = graph.accumulatedProfitGraphData;
  const values = useMemo(() => points.map((point) => point.y), [points]);
  if (values.length < 2) return null;
  const last = values[values.length - 1];
  return (
    <Spark aria-hidden={true} $hidden={hidden}>
      <NewLookSparkline
        values={values}
        color={last ? directionColor(tokens, last) : tokens.muted}
        width={SPARK_WIDTH}
        height={SPARK_HEIGHT}
      />
    </Spark>
  );
};

export const ClosedPnlChartToggle = ({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) => (
  <PhoneHeroToggle
    open={open}
    onToggle={onToggle}
    testId={PHONE_CLOSED_PNL_TOGGLE_ID}
    surface="panel"
  />
);

const PhoneClosedPnlChart = ({ graph }: { graph: ClosedPnlGraph }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const [view, setView] = useState<ChartView>("accumulated");
  return (
    <Body data-testid={PHONE_CLOSED_PNL_CHART_ID}>
      <ViewTabs role="tablist" aria-label={t("charts.view_label")}>
        {CHART_VIEWS.map((option) => (
          <ViewTab
            key={option}
            type="button"
            role="tab"
            aria-selected={option === view}
            $selected={option === view}
            onClick={() => setView(option)}
          >
            {t(CHART_VIEW_LABELS[option])}
          </ViewTab>
        ))}
      </ViewTabs>
      <ViewFade key={view}>
        <ClosedPnlChartView graph={graph} view={view} />
      </ViewFade>
      <RangesSlot>
        <PhoneHeroRanges
          options={BASE_CHART_TIME_RANGES}
          value={graph.timeRange}
          onChange={graph.setTimeRange}
          label={t("charts.range_label")}
          surface="panel"
        />
      </RangesSlot>
    </Body>
  );
};

export default PhoneClosedPnlChart;

const ClosedPnlChartView = ({
  graph,
  view,
}: {
  graph: ClosedPnlGraph;
  view: ChartView;
}) => {
  const isAccumulated = view === "accumulated";
  const data = isAccumulated
    ? graph.accumulatedProfitGraphData
    : graph.profitByDayGraphData;
  const chartProps = {
    data,
    loading: graph.isFetching && !data.length,
    displayCurrency: graph.displayCurrency,
    precision: graph.precision,
    isSuccess: graph.isSuccess,
    surface: "panel" as const,
  };
  return isAccumulated ? (
    <PhoneBrandLineChart {...chartProps} />
  ) : (
    <PhoneBrandBarChart {...chartProps} />
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;
const Spark = styled.div<{ $hidden: boolean }>`
  flex: none;
  width: ${(p) => (p.$hidden ? 0 : SPARK_WIDTH)}px;
  overflow: hidden;
  margin-bottom: 6px;
  opacity: ${(p) => (p.$hidden ? 0 : 1)};
  transition: opacity 0.2s ease, width 0.2s ease;
  svg {
    width: 100%;
  }
  @media ${NEW_LOOK_NARROW_PHONE_QUERY} {
    width: ${(p) => (p.$hidden ? 0 : NARROW_SPARK_WIDTH)}px;
  }
`;
const ViewFade = styled.div`
  animation: ${fadeIn} 0.22s ease-out;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
`;
const RangesSlot = styled.div`
  margin-top: -8px;
  padding: 0 ${NEW_LOOK_PHONE_GUTTER}px 8px;
`;
const ViewTabs = styled.div`
  display: flex;
  gap: 20px;
  padding: 0 ${NEW_LOOK_PHONE_GUTTER}px;
`;
const ViewTab = styled.button<{ $selected: boolean }>`
  position: relative;
  padding: 6px 0 8px;
  border: 0;
  background: none;
  font: inherit;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  color: ${(p) =>
    p.$selected
      ? getNewLookTokens(p.theme).accent
      : getNewLookTokens(p.theme).secondary};
  transition: color 0.2s ease;
  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    border-radius: 1px;
    background: ${(p) => getNewLookTokens(p.theme).accent};
    opacity: ${(p) => (p.$selected ? 1 : 0)};
    transform: scaleX(${(p) => (p.$selected ? 1 : 0.3)});
    transition: opacity 0.2s ease, transform 0.25s ease;
  }
`;
